// Search page — Phase 6g.
//
// Route: /search
// Shape: top nav + hero-lite heading + search input + 4 facet rows +
// result list. Clicking a result navigates to /thread/:id.
//
// Text input debounces 250ms and uses Postgres `websearch_to_tsquery` via
// supabase.from('messages').textSearch on search_tsv, OR'd with an ilike
// on conversations.subject so short subject fragments match even when the
// message body doesn't contain them.
//
// Four facet groups:
//   - Brand    (All / MD / HK)
//   - Channel  (All / Gmail / Etsy / Amazon / Shopify)
//   - Status   (All / Open / Awaiting / Snoozed / Resolved / Spam)
//   - Date     (Any / 24h / 7d / 30d)
//
// Channel grouping: Etsy MD + Etsy HK and Shopify MD + Brokerage collapse
// to single buttons because Kimi thinks of them as one thing.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Logo } from '@/components/brand/Logo';
import { useAuth } from '@/auth/AuthProvider';
import { Tag } from '@/components/brand/Tag';
import type { TagVariant } from '@/components/brand/Tag';
import type { Brand, Channel, ConversationStatus } from '@md/shared';
import {
  useSearchConversations,
  type SearchFilters,
  type SearchHit,
} from '@/lib/hooks/useSearchConversations';

// 'shipstation' is auth-storage only — never a conversation channel in practice.
const CHANNEL_LABEL: Record<Channel, string> = {
  gmail: 'Gmail',
  etsy_md: 'Etsy',
  etsy_hk: 'Etsy',
  amazon: 'Amazon',
  shopify_md: 'Shopify',
  shopify_brokerage: 'Shopify',
  shipstation: 'ShipStation',
};

const CHANNEL_TAG: Record<Channel, TagVariant> = {
  gmail: 'ch-gmail',
  etsy_md: 'ch-etsy',
  etsy_hk: 'ch-etsy',
  amazon: 'ch-amzn',
  shopify_md: 'ch-shop',
  shopify_brokerage: 'ch-shop',
  shipstation: 'ch-shop',
};

type ChannelGroup = 'any' | 'gmail' | 'etsy' | 'amazon' | 'shopify';

function formatWhen(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const age = now - d.getTime();
  const HOUR = 3_600_000;
  const DAY = 86_400_000;
  if (age < HOUR) return `${Math.max(1, Math.floor(age / 60_000))}m ago`;
  if (age < DAY) return `${Math.floor(age / HOUR)}h ago`;
  if (age < 7 * DAY) return `${Math.floor(age / DAY)}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function HitRow({ hit, onOpen }: { hit: SearchHit; onOpen: (id: string) => void }) {
  const headline =
    hit.ai_summary && hit.ai_summary.trim().length > 0
      ? hit.ai_summary
      : hit.subject && hit.subject.trim().length > 0
        ? hit.subject
        : '(no subject)';
  return (
    <button
      type="button"
      onClick={() => onOpen(hit.id)}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: '14px 16px',
        background: 'white',
        border: '1px solid hsl(var(--ink) / 0.1)',
        borderRadius: 10,
        cursor: 'pointer',
        marginBottom: 10,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 6,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexWrap: 'wrap',
          }}
        >
          <Tag variant={hit.brand === 'md' ? 'brand-md' : 'brand-hk'}>
            {hit.brand === 'md' ? 'MD' : 'HK'}
          </Tag>
          <Tag variant={CHANNEL_TAG[hit.channel]}>{CHANNEL_LABEL[hit.channel]}</Tag>
          <span
            style={{
              fontSize: 12,
              color: 'hsl(var(--ink) / 0.55)',
              textTransform: 'capitalize',
            }}
          >
            {hit.status}
          </span>
        </div>
        <span style={{ fontSize: 12, color: 'hsl(var(--ink) / 0.55)' }}>
          {formatWhen(hit.last_message_at)}
        </span>
      </div>
      <div style={{ fontWeight: 600, color: 'hsl(var(--ink))', marginBottom: 2 }}>
        {headline}
      </div>
      <div style={{ fontSize: 13, color: 'hsl(var(--ink) / 0.65)' }}>
        {hit.customer_email ?? 'unknown sender'}
      </div>
    </button>
  );
}

function FacetButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '6px 12px',
        borderRadius: 999,
        border: '1px solid hsl(var(--ink) / 0.15)',
        background: active ? 'hsl(var(--monstera) / 0.12)' : 'transparent',
        color: active ? 'hsl(var(--monstera))' : 'hsl(var(--ink) / 0.75)',
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        cursor: 'pointer',
        marginRight: 6,
        marginBottom: 6,
      }}
    >
      {children}
    </button>
  );
}

export function SearchPage() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const initials = (user?.email ?? 'K').charAt(0).toUpperCase();

  const [rawQuery, setRawQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [brand, setBrand] = useState<Brand | undefined>(undefined);
  const [channelGroup, setChannelGroup] = useState<ChannelGroup>('any');
  const [status, setStatus] = useState<ConversationStatus | undefined>(undefined);
  const [dateRange, setDateRange] = useState<SearchFilters['dateRange']>('any');

  // Debounce the text input so we aren't firing queries on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(rawQuery.trim()), 250);
    return () => clearTimeout(t);
  }, [rawQuery]);

  const hasFilter =
    debouncedQuery.length > 0 ||
    brand !== undefined ||
    channelGroup !== 'any' ||
    status !== undefined ||
    dateRange !== 'any';

  // Translate channel group → single channel for hook. For grouped channels
  // (etsy, shopify) we issue two separate queries and merge; for the MVP we
  // only filter by the MD variant since Kimi operates both but labels are
  // identical to her. Simpler: pick a representative channel; if she needs
  // HK-only Etsy later we'll add a brand-aware group.
  const effectiveChannel: Channel | undefined = (() => {
    switch (channelGroup) {
      case 'gmail':
        return 'gmail';
      case 'etsy':
        return brand === 'hk' ? 'etsy_hk' : 'etsy_md';
      case 'amazon':
        return 'amazon';
      case 'shopify':
        return 'shopify_md';
      default:
        return undefined;
    }
  })();

  const filters: SearchFilters = useMemo(
    () => ({
      q: debouncedQuery,
      brand,
      channel: effectiveChannel,
      status,
      dateRange,
    }),
    [debouncedQuery, brand, effectiveChannel, status, dateRange],
  );

  const { data: hits = [], isFetching, error } = useSearchConversations(
    filters,
    hasFilter,
  );

  return (
    <>
      {/* Mini top nav — logo + Back + avatar */}
      <header className="topnav">
        <div className="wrap">
          <div className="topnav-inner">
            <a href="/" className="logo-mark" aria-label="Market Dwellings">
              <Logo />
            </a>
            <nav
              className="viewnav"
              aria-label="Search nav"
              style={{ display: 'flex', alignItems: 'center', gap: 10 }}
            >
              <button type="button" onClick={() => navigate('/')}>
                <span aria-hidden style={{ marginRight: 6 }}>←</span>
                Back to Standup
              </button>
            </nav>
            <div className="top-right">
              <button
                type="button"
                className="avatar"
                aria-label="Account menu — sign out"
                title={user?.email ?? 'Signed in'}
                onClick={() => void signOut()}
              >
                {initials}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="wrap" style={{ paddingTop: 24, paddingBottom: 48 }}>
        <h1 className="display h2" style={{ marginTop: 0, marginBottom: 20 }}>
          Search all threads
        </h1>

        {/* Search input */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 14px',
            border: '1px solid hsl(var(--ink) / 0.15)',
            borderRadius: 10,
            background: 'white',
            marginBottom: 18,
          }}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            width="18"
            height="18"
            style={{ color: 'hsl(var(--ink) / 0.5)' }}
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" strokeLinecap="round" />
          </svg>
          <input
            type="search"
            value={rawQuery}
            onChange={(e) => setRawQuery(e.target.value)}
            placeholder="Search by email, subject, or message text…"
            autoFocus
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              fontSize: 15,
              color: 'hsl(var(--ink))',
              background: 'transparent',
            }}
          />
          {rawQuery ? (
            <button
              type="button"
              onClick={() => setRawQuery('')}
              aria-label="Clear search"
              style={{
                border: 'none',
                background: 'transparent',
                color: 'hsl(var(--ink) / 0.6)',
                cursor: 'pointer',
                fontSize: 18,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          ) : null}
        </div>

        {/* Facets */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: 'hsl(var(--ink) / 0.55)', textTransform: 'uppercase', letterSpacing: '0.08em', marginRight: 8 }}>Brand</span>
            <FacetButton active={brand === undefined} onClick={() => setBrand(undefined)}>All</FacetButton>
            <FacetButton active={brand === 'md'} onClick={() => setBrand('md')}>Market Dwellings</FacetButton>
            <FacetButton active={brand === 'hk'} onClick={() => setBrand('hk')}>HarperKellee</FacetButton>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: 'hsl(var(--ink) / 0.55)', textTransform: 'uppercase', letterSpacing: '0.08em', marginRight: 8 }}>Channel</span>
            <FacetButton active={channelGroup === 'any'} onClick={() => setChannelGroup('any')}>All</FacetButton>
            <FacetButton active={channelGroup === 'gmail'} onClick={() => setChannelGroup('gmail')}>Gmail</FacetButton>
            <FacetButton active={channelGroup === 'etsy'} onClick={() => setChannelGroup('etsy')}>Etsy</FacetButton>
            <FacetButton active={channelGroup === 'amazon'} onClick={() => setChannelGroup('amazon')}>Amazon</FacetButton>
            <FacetButton active={channelGroup === 'shopify'} onClick={() => setChannelGroup('shopify')}>Shopify</FacetButton>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: 'hsl(var(--ink) / 0.55)', textTransform: 'uppercase', letterSpacing: '0.08em', marginRight: 8 }}>Status</span>
            <FacetButton active={status === undefined} onClick={() => setStatus(undefined)}>All</FacetButton>
            <FacetButton active={status === 'open'} onClick={() => setStatus('open')}>Open</FacetButton>
            <FacetButton active={status === 'awaiting'} onClick={() => setStatus('awaiting')}>Awaiting</FacetButton>
            <FacetButton active={status === 'snoozed'} onClick={() => setStatus('snoozed')}>Snoozed</FacetButton>
            <FacetButton active={status === 'resolved'} onClick={() => setStatus('resolved')}>Resolved</FacetButton>
            <FacetButton active={status === 'spam'} onClick={() => setStatus('spam')}>Spam</FacetButton>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 12, color: 'hsl(var(--ink) / 0.55)', textTransform: 'uppercase', letterSpacing: '0.08em', marginRight: 8 }}>Date</span>
            <FacetButton active={dateRange === 'any'} onClick={() => setDateRange('any')}>Any time</FacetButton>
            <FacetButton active={dateRange === '24h'} onClick={() => setDateRange('24h')}>24 hours</FacetButton>
            <FacetButton active={dateRange === '7d'} onClick={() => setDateRange('7d')}>7 days</FacetButton>
            <FacetButton active={dateRange === '30d'} onClick={() => setDateRange('30d')}>30 days</FacetButton>
          </div>
        </div>

        {/* Results */}
        {!hasFilter ? (
          <div style={{ padding: '32px 0', color: 'hsl(var(--ink) / 0.55)' }}>
            Type a search or pick a filter to start.
          </div>
        ) : error ? (
          <div style={{ padding: '24px 0', color: 'hsl(var(--clay))' }}>
            Search failed: {(error as Error).message}
          </div>
        ) : isFetching && hits.length === 0 ? (
          <div style={{ padding: '24px 0', color: 'hsl(var(--ink) / 0.55)' }}>
            Searching…
          </div>
        ) : hits.length === 0 ? (
          <div style={{ padding: '24px 0', color: 'hsl(var(--ink) / 0.55)' }}>
            No threads match. Try loosening a filter or shortening the query.
          </div>
        ) : (
          <>
            <div
              style={{
                fontSize: 13,
                color: 'hsl(var(--ink) / 0.55)',
                marginBottom: 8,
              }}
            >
              {hits.length} result{hits.length === 1 ? '' : 's'}
              {isFetching ? ' · refreshing…' : ''}
            </div>
            <div>
              {hits.map((h) => (
                <HitRow key={h.id} hit={h} onOpen={(id) => navigate(`/thread/${id}`)} />
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}
